//@flow
import * as React from 'react';
import { themr } from 'react-css-themr';
import { compose } from 'recompose';
import { translate } from 'react-i18next';
import classnames from 'classnames';

import OverlayDialog from 'common/components/core/dialog/OverlayDialog';
import { HeaderAppBar } from 'common/components/core/app_bar/HeaderAppBar';
import { Button, ButtonTypes } from 'common/components/core/button';
import Steps from 'common/components/core/steps';

import theme from 'common/components/core/dialog/Dialog.scss';
import { dashboardColors } from 'common/enums/ColorEnum';
import DefaultActions from 'common/components/core/actionButtons';
import coreIds from 'common/tests/core/coreIds.json';

type Props = {
    active: boolean,
    theme: Object,
    title: string | any,
    subtitle?: string | any,
    acceptActionLabel?: string,
    cancelActionLabel?: string,
    isLoading?: boolean,
    className?: string,
    testId?: string,
    'data-test-id'?: string,
    headerColor: string,
    dialogClassName?: string,
    dialogContentClassName?: string,
    steps?: Array<Object>,
    showSteps?: boolean,
    enableCloseOnEsc?: boolean,
    disableAccept?: boolean,
    disableCancel?: boolean,
    currentStep?: number,
    onDismiss: () => void,
    onCancel?: () => void,
    onAccept: () => void
};

type dialogFactoryProps = {
    AppBar?: Function | React.ComponentType<any>,
    InnerContent?: Function | React.ComponentType<any>,
    Actions?: Function | React.ComponentType<any>,
    options: Object
};

const DefaultInnerContent = ({ children, theme }) => {
    return <div className={theme.dialogInnerContent}>{children}</div>;
};

const dialogFactory = ({
    AppBar = HeaderAppBar,
    InnerContent = DefaultInnerContent,
    Actions = DefaultActions,
    options = {}
}: dialogFactoryProps) => {
    const { undecorated = false } = options;
    class Dialog extends React.PureComponent<Props, void> {
        props: Props;
        static defaultProps = {
            acceptActionColor: dashboardColors.darkSkyBlue.name,
            acceptButtonType: ButtonTypes.Solid,
            headerColor: dashboardColors.duskBlue.name,
            enableCloseOnEsc: false
        };
        render() {
            const {
                title,
                subtitle,
                active,
                theme,
                headerColor,
                onDismiss,
                className,
                dialogClassName,
                dialogContentClassName,
                steps,
                showSteps,
                currentStep,
                enableCloseOnEsc,
                testId,
                'data-test-id': dataTestId
            } = this.props;
            const dialogCloseActionIcon = (
                <Button
                    className={theme.closeIcon}
                    inverted
                    testId={coreIds.cancel}
                    icon="cancel-thin"
                    buttonType={ButtonTypes.Icon}
                    onClick={onDismiss}
                    ripple={false}
                />
            );
            return (
                <OverlayDialog
                    data-test-id={dataTestId}
                    testId={testId}
                    className={className}
                    dialogClassName={dialogClassName}
                    active={active}
                    theme={theme}
                    onEscapeClick={enableCloseOnEsc && onDismiss}
                >
                    <AppBar
                        flat
                        title={title}
                        subtitle={subtitle}
                        theme={theme}
                        actions={dialogCloseActionIcon}
                        headerColor={headerColor}
                    />
                    {showSteps ? (
                        <Steps
                            currentStep={currentStep}
                            showSteps={showSteps}
                            steps={steps}
                        />
                    ) : null}
                    <div
                        className={classnames(
                            dialogContentClassName,
                            theme.dialogContent
                        )}
                    >
                        <InnerContent {...this.props} />
                    </div>
                    <Actions {...this.props} />
                </OverlayDialog>
            );
        }
    }

    return undecorated
        ? Dialog
        : compose(themr('Dialog', theme), translate())(Dialog);
};
const Dialog = dialogFactory({
    InnerContent: DefaultInnerContent,
    Actions: DefaultActions,
    options: {}
});
export default Dialog;
export { dialogFactory, Dialog };
